<script setup>
/* eslint semi: 1 */

import { computed, inject } from 'vue';
import { useForm } from 'vee-validate';
import { toTypedSchema } from '@vee-validate/yup';
import * as yup from 'yup';
import { t } from '@/i18n/i18ns';
import { signUp as AmplifySignUp } from 'aws-amplify/auth';

import FormInput from '@/components/common/form/form-input-tmpl.vue';
import FormSelect from '@/components/common/form/form-select-tmpl.vue';
import FormButton from '@/components/common/form/form-button-tmpl.vue';
import SignInWithSocialAccount from '@/components/common/sign-in/sign-in-with-social-account-tmpl.vue';
import CONSTANTS from '@/globals/constants';

// Mixin imports
import AuthMixin from '@/components/mixins/auth-mixin';

import { useCognitoAuthStore } from '@base-shared/stores/cognito-auth';

const $utils = inject('$utils');

// Component Options added so we can get access to mixins inside <script setup>
defineOptions({
  mixins: [AuthMixin]
});

// Props
defineProps({
  isSignUpMini: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['successSubmit', 'errorSubmit', 'redirectToSignIn']);

const schema = yup.object({
  firstName: yup.string()
    .required('label.requiredFirstName'),
  lastName: yup.string()
    .required('label.requiredLastName'),
  email: yup.string()
    .email('label.invalidEmail')
    .transform((value) => value?.toLowerCase?.().trim?.() ?? value)
    .required('label.requiredEmail'),
  password: yup.string()
    .required('label.requiredPassword')
    .matches(CONSTANTS.PASSWORD_REGEX, 'label.passwordHint'),
  dataRegion: yup.string()
    .required('label.requiredDataStorageRegion')
});

const {
  errors: formErrors,
  handleSubmit,
  isSubmitting,
  defineField
} = useForm({
  validationSchema: toTypedSchema(schema)
});

const [firstName] = defineField('firstName');
const [lastName] = defineField('lastName');
const [email] = defineField('email');
const [password] = defineField('password');
const [dataRegion] = defineField('dataRegion');

const cognitoAuthStore = useCognitoAuthStore();

// Get the formatted stages from the utils that formats the stages
// into an object with region codes as keys and stage objects as values
const stages = computed(() => $utils.getFormattedStages());

// Get the selected region from the selected dataRegion as region code
// and get the stage value and return the GLOBAL_STAGE as default
const selectedStage = computed(() => dataRegion.value && stages.value && Object.keys(stages.value).length
  ? stages.value[dataRegion.value]?.stage ?? CONFIG.GLOBAL_STAGE
  : CONFIG.GLOBAL_STAGE
);

// Convert the formatted stages into an array of objects with label and value
// for the select input
const dataRegionOptions = computed(() => Object
  .keys(stages.value)
  .map((key) => ({
    label: t(stages.value[key].translationKey),
    value: stages.value[key].code
  }))
);

const performSignUp = async (data) => {
  await cognitoAuthStore.configureWithCognitoStage(selectedStage.value);
  return await AmplifySignUp({
    username: data.email,
    password: data.password,
    options: {
      userAttributes: {
        given_name: data.firstName,
        family_name: data.lastName
      },
      clientMetadata: {
        signUpSource: $utils.isPageForLicenses()
          ? CONSTANTS.PRODUCT_ACTIVATE
          : CONSTANTS.PRODUCT_ARYA
      }
    }
  });
};

function onRedirectToSignIn () {
  if (isSubmitting.value) {
    return;
  }

  emit('redirectToSignIn');
}

const handleSubmitSuccess = async (values, actions) => {
  // set the transformed email by vee-validate yup schema
  // to the email field
  actions.setFieldValue('email', values.email);

  try {
    const { nextStep } = await performSignUp(values);

    if (nextStep && (nextStep.signUpStep === 'CONFIRM_SIGN_UP' || nextStep.signUpStep === 'DONE')) {
      emit('successSubmit', {
        data: {
          firstName: values.firstName,
          lastName: values.lastName,
          email: values.email,
          password: values.password,
          dataRegion: values.dataRegion,
          stage: selectedStage.value
        },
        autoVerifySignUpSuccessful: nextStep && nextStep.signUpStep === 'DONE'
      });
    }
  } catch (error) {
    switch (error.name) {
      case CONSTANTS.USERNAME_EXISTS_EXCEPTION:
        actions.setFieldError('email', 'label.signUpErrorMessageAccountEmailExist');
        break;
      case 'UserLambdaValidationException':
        actions.setFieldError(
          'email',
          error.message && error.message.includes('e-mail already exists')
            ? 'label.signUpErrorMessageAccountEmailExist'
            : error.message
        );
        break;
      case CONSTANTS.INVALID_PASSWORD:
        actions.setFieldError('password', 'label.passwordHint');
        break;
      case CONSTANTS.INVALID_PARAMETER_EXCEPTION:
        if (
          (error.message.toLowerCase().includes('expression pattern') && error.message.toLowerCase().includes('username')) ||
        error.message.toLowerCase().includes('email') ||
        error.message.toLowerCase().includes('should be an email')
        ) {
          actions.setFieldError('email', 'label.invalidEmail');
        } else if (error.message.toLowerCase().includes('password')) {
          actions.setFieldError('password', 'label.passwordHint');
        } else {
          actions.setErrors({
            firstName: ' ',
            lastName: ' ',
            email: ' ',
            dataRegion: ' ',
            password: error.message
          });
        }
        break;
      default:
        actions.setErrors({
          firstName: ' ',
          lastName: ' ',
          email: ' ',
          dataRegion: ' ',
          password: error.message
        });
        break;
    }
  }
};

const onSubmit = handleSubmit(handleSubmitSuccess);
</script>

<template>
  <form
    class="flex flex-col flex-1 w-full justify-between"
    data-component-element-id="sign-up-form"
    @submit="onSubmit"
  >

    <div class="flex flex-col">
      <label
        class="max-[1919px]:text-[10px]/[10px] min-[1920px]:text-xs uppercase"
        :class="{
          'cursor-pointer text-white': !isSubmitting,
          'cursor-not-allowed text-gray-400': isSubmitting
        }"
        v-html="$t('label.alreadyHaveAnAccountRedirect', { linkStart: `<a href='#' class='underline text-white'>`, linkEnd: '</a>' })"
        @click="onRedirectToSignIn"
      >
      </label>

      <!-- Social Sign In -->
      <sign-in-with-social-account
        class="max-[1919px]:mt-3 min-[1920px]:mt-5 flex flex-col gap-2"
        ref="socialSignIn"
        type="signup"
        :disabled="isSubmitting"
      />
    </div>

    <div class="flex flex-col px-3 max-[1919px]:gap-5 min-[1920px]:gap-6">
      <form-input
        v-model="email"
        id="email"
        data-component-element-id="sign-up-email-input"
        type="email"
        inputTheme="light"
        required
        autocomplete="username"
        :disabled="isSubmitting"
        :size="isSignUpMini ? 'xs' : 'md'"
        :label="$t('label.emailText')"
        :validationStatus="formErrors?.email ? 'error' : undefined"
      >
        <template
          v-if="formErrors?.email"
          #validationTransparent
        >
          {{ $t(formErrors.email) }}
        </template>
      </form-input>

      <form-input
        v-model="password"
        id="password"
        data-component-element-id="sign-up-password-input"
        type="password"
        inputTheme="light"
        required
        autocomplete="new-password"
        :disabled="isSubmitting"
        :size="isSignUpMini ? 'xs' : 'md'"
        :label="$t('label.passwordText')"
        :validationStatus="formErrors?.password ? 'error' : undefined"
      >
        <template
          v-if="formErrors?.password"
          #validationTransparent
        >
          {{ $t(formErrors.password) }}
        </template>
      </form-input>

      <form-input
        v-model="firstName"
        id="first-name"
        data-component-element-id="sign-up-first-name-input"
        type="text"
        inputTheme="light"
        required
        autocomplete="given-name"
        :disabled="isSubmitting"
        :size="isSignUpMini ? 'xs' : 'md'"
        :label="$t('label.firstName')"
        :validationStatus="formErrors?.firstName ? 'error' : undefined"
      >
        <template
          v-if="formErrors?.firstName"
          #validationTransparent
        >
          {{ $t(formErrors.firstName) }}
        </template>
      </form-input>

      <form-input
        v-model="lastName"
        id="last-name"
        data-component-element-id="sign-up-last-name-input"
        type="text"
        inputTheme="light"
        required
        autocomplete="family-name"
        :disabled="isSubmitting"
        :size="isSignUpMini ? 'xs' : 'md'"
        :label="$t('label.lastName')"
        :validationStatus="formErrors?.lastName ? 'error' : undefined"
      >
        <template
          v-if="formErrors?.lastName"
          #validationTransparent
        >
          {{ $t(formErrors.lastName) }}
        </template>
      </form-input>

      <div class="flex items-center gap-2 w-full">
        <form-select
          v-model="dataRegion"
          id="data-region"
          data-component-element-id="sign-up-data-region-select"
          inputTheme="light"
          required
          :disabled="isSubmitting"
          :size="isSignUpMini ? 'xs' : 'md'"
          :label="$t('label.dataStorageRegion')"
          :validationStatus="formErrors?.dataRegion ? 'error' : undefined"
          :options="dataRegionOptions"
          :disableLabelScaling="false"
          class="flex-1"
        >
          <template
            v-if="formErrors?.dataRegion"
            #validationTransparent
          >
            {{ $t(formErrors.dataRegion) }}
          </template>
        </form-select>

        <el-popover
          popper-class="custom-popover-popper !p-4 !text-xs"
          placement="right"
          width="255"
          trigger="hover">
          <template #reference>
            <el-icon class="!text-lg !text-white hover:!text-gray-400">
              <QuestionFilled />
            </el-icon>
          </template>
          <template #default>
            <p>{{ $t('texts.dataStorageRegionHint') }}</p>
          </template>
        </el-popover>
      </div>
    </div>

    <form-button
      type="submit"
      button-theme="light"
      data-component-element-id="sign-up-form-submit-btn"
      :size="isSignUpMini ? 'xs' : 'md'"
      :disabled="isSubmitting"
      :loading="isSubmitting"
      :label="$t('label.signUp')"
      @click="onSubmit"
    />
  </form>
</template>
